import { APP_ROUTES } from '../constants/navigation';
import { useNavigate } from 'react-router-dom';
import { WebstoreService as SharedWebstoreService } from '../lib/apis/WebstoreService';
import { WebstoreService as VendorWebstoreService } from '../../vendor/lib/apis/WebstoreService';
import { VendorService } from '../../vendor/lib/apis/VendorService';
import { usePearlToast } from 'framework';
import { unwrapResponse } from '../../lib/apis/utils';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/Store';
import { StorageService, StorageKeys } from '../../lib/StorageService';
import { TokenService } from '../../lib/apis/TokenService';
import { redirectToVendorConnectOrFallback } from '../lib/VendorConnectRedirect';
import { COMPLETE_STEP } from '../../vendor/constants/WebstoreSetupSteps';

const getClaimVerificationEmail = (webstoreToClaim) =>
    webstoreToClaim?.verificationEmailSentTo || webstoreToClaim?.CybContactEmail || null;

const mergeVerificationEmailIntoClaimable = (webstoreToClaim, beginClaimResult) => {
    const verificationEmailSentTo = beginClaimResult?.verificationEmailSentTo;
    if (!verificationEmailSentTo) {
        return webstoreToClaim;
    }
    return {
        ...webstoreToClaim,
        verificationEmailSentTo,
        CybContactEmail: verificationEmailSentTo,
    };
};

const extractClaimErrorKey = (error) => {
    const raw = error?.cause?.message || error?.message || error;
    const message =
        typeof raw === 'string'
            ? raw
            : typeof raw?.message === 'string'
            ? raw.message
            : typeof raw?.error === 'string'
            ? raw.error
            : '';

    const normalized = message.toLowerCase();
    if (normalized.includes('invalid code')) return 'invalid';
    if (normalized.includes('expired code') || normalized.includes('expired')) return 'expired';
    if (normalized.includes('must belong to a cyb vendor')) return 'already_claimed';
    return null;
};

const useClaimYourBusiness = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const sharedWebstoreService = SharedWebstoreService();
    const vendorWebstoreService = VendorWebstoreService();
    const vendorService = VendorService();
    const storageService = StorageService();

    // useWebstoreContext breaks here for some reason
    // error mentions translations
    // useStore instead, but maybe fix later?
    const { authUser, vendor, webstore } = useStore();

    const { toastError } = usePearlToast();

    const getClaimableStore = () => {
        const stringified = storageService.get(StorageKeys.cybInProgressWebstore);
        return JSON.parse(stringified);
    };

    const setClaimableStore = (webstoreToClaim) => {
        storageService.set(StorageKeys.cybInProgressWebstore, JSON.stringify(webstoreToClaim));
    };

    const clearClaimableStore = () => {
        storageService.remove(StorageKeys.cybInProgressWebstore);
    };

    const showClaimPage = (webstoreToClaim) => {
        setClaimableStore(webstoreToClaim);
        if (webstoreToClaim.IsCYB) {
            navigate(APP_ROUTES.claimBusiness.helpfulInformation);
        } else {
            navigate(APP_ROUTES.claimBusiness.claimForm, {
                state: {
                    typeOfForm: 'Already',
                    webstoreToClaim: webstoreToClaim,
                },
            });
        }
    };

    const showSendEmailPage = (webstoreToClaim) => {
        setClaimableStore(webstoreToClaim);
        if (webstoreToClaim.IsCYB) {
            navigate(APP_ROUTES.claimBusiness.sendEmail);
        } else {
            navigate(APP_ROUTES.claimBusiness.claimForm, {
                state: {
                    typeOfForm: 'Already',
                    webstoreToClaim: webstoreToClaim,
                },
            });
        }
    };

    const sendVerificationCode = async (webstoreToClaim) => {
        setClaimableStore(webstoreToClaim);
        try {
            const beginClaimResult = await sharedWebstoreService.beginClaimWebstore(
                webstoreToClaim.Id
            );
            const updatedWebstore = mergeVerificationEmailIntoClaimable(
                webstoreToClaim,
                beginClaimResult
            );
            setClaimableStore(updatedWebstore);
            return updatedWebstore;
        } catch (e) {
            toastError();
            throw e;
        }
    };

    const beginClaim = async (webstoreToClaim) => {
        const updatedWebstore = await sendVerificationCode(webstoreToClaim);
        navigate(APP_ROUTES.claimBusiness.verifyEmail);
        return updatedWebstore;
    };

    const resendCode = async (webstoreToClaim) => sendVerificationCode(webstoreToClaim);

    const completeClaim = async (webstoreToClaim, code, { redirectAfterClaim = true } = {}) => {
        let claimSucceeded = false;
        try {
            await sharedWebstoreService.claimWebstore(webstoreToClaim.Id, code);
            claimSucceeded = true;

            const webstoreIdToRefresh =
                webstoreToClaim.DraftWebstoreId ?? webstoreToClaim.Id ?? webstore.Id;

            const refreshWebstoreAndVendor = async () => {
                if (!webstoreIdToRefresh) return;
                const webstoreRes = await vendorWebstoreService.getWebstoreById(
                    webstoreIdToRefresh,
                    {
                        removeInvalidImgSrc: false,
                    }
                );
                const updatedWebstore = unwrapResponse(webstoreRes);
                webstore.clear();
                webstore.setFromApi(updatedWebstore);

                const updatedVendor = await vendorService.getVendorByUsername(authUser.Username);
                if (updatedVendor) {
                    vendor.updateVendor(updatedVendor);
                    authUser.Roles.Vendor = true;
                }
            };

            const makeClaimedWebstoreDefault = async () => {
                if (!vendor?.id || !webstoreToClaim?.Id) return;
                vendor.props = vendor.props || {};
                vendor.props.defaultWebstoreId = webstoreToClaim.Id;
                const patchVendorRes = await vendorService.patchVendor({
                    id: vendor.id,
                    props: vendor.props,
                });
                if (patchVendorRes?.ok) {
                    const patchedVendor = unwrapResponse(patchVendorRes);
                    if (patchedVendor) vendor.updateVendor(patchedVendor);
                }
            };

            const markSetupComplete = async () => {
                webstore.CurrentSetupStep = COMPLETE_STEP;
                const patchRes = await vendorWebstoreService.patchWebstore(
                    webstore.forPatchSetupStep
                );
                if (patchRes?.ok) {
                    webstore.setFromApi(unwrapResponse(patchRes));
                }
            };

            try {
                await refreshWebstoreAndVendor();
            } catch {}

            try {
                await makeClaimedWebstoreDefault();
            } catch {}

            try {
                await markSetupComplete();
            } catch {}

            clearClaimableStore();
            if (redirectAfterClaim) {
                redirectToVendorConnectOrFallback(TokenService(), navigate);
            }
        } catch (e) {
            const claimErrorKey = extractClaimErrorKey(e);
            if (!claimSucceeded) {
                if (claimErrorKey === 'expired') {
                    toastError(t('Pages.ClaimVerifyYourId.CodeExpired'));
                } else if (claimErrorKey === 'already_claimed') {
                    toastError(t('Pages.ClaimVerifyYourId.AlreadyClaimed'));
                } else {
                    toastError(t('Pages.ClaimVerifyYourId.CodeError'));
                }
            } else {
                toastError();
            }
            throw e;
        }
    };

    return {
        getClaimableStore,
        setClaimableStore,
        showClaimPage,
        showSendEmailPage,
        beginClaim,
        sendVerificationCode,
        resendCode,
        completeClaim,
        clearClaimableStore,
    };
};

export { useClaimYourBusiness, getClaimVerificationEmail, mergeVerificationEmailIntoClaimable };
